/**
 * EPC List Page
 * List and manage EPCs (Equipamentos de Proteção Coletiva)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import {
  ActionButton,
  PageShell,
  DataTable,
  type DataTableColumn,
  DeleteWithDependenciesDialog,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { epcService } from '../../services/epcService';
import { Epc, EpcDependencies, EpcFilters } from '../../types/epc';
import { dependencyItems } from '../../utils/dependencyItems';
import { EPCFiltersComponent, EpcFiltersData } from './components/EPCFilters';
import EPCImportDialog from './components/EPCImportDialog';
import { TipoDeRiscoChip } from '../epis/components/TipoDeRiscoChip';
import { ValidadeStatusChip } from './components/ValidadeStatusChip';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';

const columns: DataTableColumn<Epc>[] = [
  {
    id: 'nome',
    label: 'Nome',
    primary: true,
    render: (epc) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {epc.nome}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {epc.risco}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'tipoDeRisco',
    label: 'Tipo de Risco',
    render: (epc) => <TipoDeRiscoChip tipo={epc.tipoDeRisco} />,
  },
  {
    id: 'validade',
    label: 'Validade',
    render: (epc) => <ValidadeStatusChip validade={epc.validade} />,
  },
  {
    id: 'proporcional',
    label: 'Proporcional',
    render: (epc) =>
      epc.proporcional ? (
        <Chip
          label={epc.proporcional}
          size="small"
          color={epc.proporcional === 'Sim' ? 'success' : 'default'}
          variant="outlined"
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      ),
  },
  {
    id: 'quantidade',
    label: 'Qtd',
    render: (epc) =>
      epc.quantidade !== null && epc.quantidade !== undefined ? (
        <Typography variant="body2">
          {epc.quantidade} {epc.unidade || ''}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      ),
  },
];

export const EPCListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/epcs/:id/edit', pode('epc.update'), 'Editar');
  const excluir = useAcao('epc.delete', 'Excluir');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [epcs, setEpcs] = useState<Epc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<EpcFilters>({});

  const loadEpcs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await epcService.getEpcs(params);
      setEpcs(response.epcs);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar EPCs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadEpcs();
  }, [loadEpcs]);

  const handleFilter = useCallback((data: EpcFiltersData) => {
    const filters: EpcFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.tipoDeRisco) filters.tipoDeRisco = data.tipoDeRisco as EpcFilters['tipoDeRisco'];
    if (data.expired) filters.expired = true;
    if (data.expiringSoon) filters.expiringSoon = true;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<Epc, EpcDependencies>({
      checkDependencies: (id) => epcService.checkDependencies(id),
      deleteEntity: (id) => epcService.deleteEpc(id),
      checkErrorMessage: 'Erro ao verificar vínculos do EPC',
      deleteErrorMessage: 'Erro ao excluir EPC',
      onError: setError,
      onDeleted: loadEpcs,
    });

  const handleEdit = (epc: Epc) => {
    navigate(`/epcs/${epc.id}/edit`);
  };

  const handleExport = async () => {
    try {
      const blob = await epcService.exportEpcs();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'epcs-export.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao exportar EPCs');
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'EPCs' },
      ]}
      title="Gestão de EPCs"
      subtitle="Gerencie os Equipamentos de Proteção Coletiva"
      actions={
        <>
          <ActionButton
            acao="epc.read"
            rotulo="Exportar"
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Exportar
          </ActionButton>
          <ActionButton
            acao="epc.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/epcs/new"
            exige={pode('epc.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo EPC
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <EPCFiltersComponent onFilter={handleFilter} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={epcs}
        getRowId={(epc) => epc.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum EPC encontrado',
          description:
            'EPCs são registrados nesta tela e depois vinculados como proteção dos riscos ocupacionais.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      <EPCImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={() => {
          setImportDialogOpen(false);
          loadEpcs();
        }}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir EPC"
        confirmText="Tem certeza que deseja excluir este EPC?"
        prerequisite={{
          title: 'Este EPC está em uso',
          reason: 'EPCs com aplicações ativas não podem ser excluídos — remova as aplicações primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Ver aplicações',
            permission: 'epc.update',
            mode: 'navigate',
            route: `/risk-applications?epcId=${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para remover as aplicações deste EPC antes de excluí-lo.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default EPCListPage;
